import React, { SyntheticEvent, useCallback } from "react";
import { useField } from "react-final-form";
import { Autocomplete, ListItemText, MenuItem, TextField } from "@mui/material";
import fuzzysort from "fuzzysort";

import { ScoringInputConfig } from "fond/cityPlanner/assessConfigurationHelper";

interface CandidateItemProps {
  item: ScoringInputConfig;
}

const CandidateItem: React.FC<CandidateItemProps> = ({ item }: CandidateItemProps) => {
  useField(item.id); // register the field for updates
  const primaryId = `candidate-item-primary-${item.id}`;

  return (
    <ListItemText
      primary={item.title}
      secondary={item.description}
      primaryTypographyProps={{ id: primaryId }}
      secondaryTypographyProps={{ variant: "caption", sx: { whiteSpace: "normal" } }}
    />
  );
};

interface ScoringItemsSearchMenuProps {
  candidateItems: ScoringInputConfig[];
  onSelect: (itemId: string) => void;
  onClose: () => void;
  onBack: () => void;
}

const ScoringItemsSearchMenu: React.FC<ScoringItemsSearchMenuProps> = ({
  candidateItems,
  onSelect,
  onClose,
  onBack,
}: ScoringItemsSearchMenuProps) => {
  const handleChange = useCallback(
    (_event: SyntheticEvent, newValue: ScoringInputConfig | null) => {
      if (newValue) {
        onSelect(newValue.id);
        onClose();
      }
    },
    [onSelect, onClose]
  );

  return (
    <MenuItem dense sx={{ px: 2, py: 1, width: "45ch" }} onKeyDown={(e) => e.stopPropagation()}>
      <Autocomplete<ScoringInputConfig>
        fullWidth
        open
        autoFocus
        options={candidateItems}
        getOptionLabel={(option) => option.title}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        value={null}
        renderInput={(params) => <TextField {...params} autoFocus variant="outlined" />}
        renderOption={(props, option) => {
          return (
            <li {...props} key={option.id}>
              <CandidateItem item={option} />
            </li>
          );
        }}
        filterOptions={(options, state) => {
          const results = fuzzysort.go(state.inputValue, options, { key: "title", all: true });
          return results.map((result) => result.obj);
        }}
        onChange={handleChange}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            onBack();
          }
        }}
        size="small"
      />
    </MenuItem>
  );
};

export default ScoringItemsSearchMenu;
